import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Observable, from } from 'rxjs';
import { switchMap, catchError } from 'rxjs/operators';
import { RECAPTCHA_V3_SITE_KEY } from 'ng-recaptcha';

declare global {
  interface Window {
    grecaptcha?: {
      ready: (callback: () => void) => void;
      execute: (siteKey: string, options: { action: string }) => Promise<string>;
      enterprise?: {
        ready: (callback: () => void) => void;
        execute: (siteKey: string, options: { action: string }) => Promise<string>;
      };
    };
  }
}

const ENTERPRISE_SCRIPT_URL = 'https://www.google.com/recaptcha/enterprise.js';

@Injectable()
export class RecaptchaEnterpriseService {
  private static loadPromise: Promise<void> | null = null;
  private static siteKeyUsed: string | null = null;

  constructor(
    @Inject(PLATFORM_ID) private platformId: object,
    @Inject(RECAPTCHA_V3_SITE_KEY) private siteKey: string
  ) {}

  /**
   * Execute reCAPTCHA Enterprise and return the token for the given action.
   * Matches backend actions: login, signup, broker_carrier_get, company_driver_get, etc.
   */
  execute(action: string): Observable<string> {
    if (!isPlatformBrowser(this.platformId)) {
      return from(Promise.resolve(''));
    }
    return from(this.ensureScriptLoaded()).pipe(
      switchMap(() => this.getToken(action)),
      catchError((err) => {
        console.error('RecaptchaEnterpriseService execute error', err);
        throw err;
      })
    );
  }

  private ensureScriptLoaded(): Promise<void> {
    if (typeof window === 'undefined' || !window.document) {
      return Promise.reject(new Error('Not in browser'));
    }
    const w = window as Window & { grecaptcha?: Window['grecaptcha'] };
    const g = w.grecaptcha;
    if (g?.enterprise?.execute) {
      return Promise.resolve();
    }
    if (RecaptchaEnterpriseService.loadPromise) {
      return RecaptchaEnterpriseService.loadPromise;
    }
    const key = this.siteKey;
    if (!key) {
      return Promise.reject(new Error('RECAPTCHA_V3_SITE_KEY is not set'));
    }
    RecaptchaEnterpriseService.siteKeyUsed = key;
    RecaptchaEnterpriseService.loadPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = `${ENTERPRISE_SCRIPT_URL}?render=${encodeURIComponent(key)}`;
      script.async = false;
      script.defer = false;
      script.onload = () => {
        const g = (window as Window & { grecaptcha?: Window['grecaptcha'] }).grecaptcha;
        if (g?.enterprise?.ready) {
          g.enterprise.ready(() => resolve());
        } else {
          resolve();
        }
      };
      script.onerror = () => reject(new Error('Failed to load reCAPTCHA Enterprise script'));
      document.head.appendChild(script);
    });
    return RecaptchaEnterpriseService.loadPromise;
  }

  private getToken(action: string): Promise<string> {
    const w = window as Window;
    const g = w.grecaptcha;
    if (!g?.enterprise) {
      return Promise.reject(new Error('grecaptcha.enterprise not available'));
    }
    return new Promise((resolve, reject) => {
      g.enterprise!.ready(() => {
        g.enterprise!.execute(this.siteKey, { action })
          .then(resolve)
          .catch(reject);
      });
    });
  }
}